import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Breadcrumbs,
  Button,
  Heading,
  Icon,
  Link,
  RyeconLineage,
  Sizes,
} from '@dbt-labs/sourdough';

import { ProjectHasNoEnvironments } from '@/components/ErrorScreens/ProjectErrors';
import { Page } from '@/components/Page';
import { ProjectDescriptionBanner } from '@/components/ProjectDescriptionBanner';
import { XPIndicatorFn } from '@/components/XPIndicator';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { getDefaultAccountFilters } from '@/context/FilterProvider';
import { useFavoriteProjects } from '@/hooks/admin/favoriteProjects';
import { useRecommendationsCount } from '@/hooks/discovery/recommendationsCount';
import { useLinkToExplorer } from '@/hooks/links';
import { useRawParams, useUrlProjectParams } from '@/hooks/params';
import { useProjectBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { LineageAdUnit } from '@/pages/ProjectOverview/LineageAdUnit';
import { FavoriteProjectToggle } from '../components/FavoriteToggle';
import { LatestUpdates } from './components/LatestUpdates';
import { Marts } from './components/Marts';
import { ProjectAssets } from './components/ProjectAssets';
import ProjectOverviewTabs, { ProjectTabInfo } from './components/ProjectOverviewTabs';
import { PublicModelsAndMarts } from './components/PublicModelsAndMarts';
import { PerformancePageContent } from './performance/PerformancePage';
import { RecommendationsPageContent } from './recommendations/RecommendationsPage';

export const ProjectOverviewPage: FC = () => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const { accountId, projectName, determiningProjectAccess } = useDbtCloudProject();

  const breadcrumbs = useProjectBreadcrumbs();

  const isCatalogPreviewEnabled = hasAccountFeature('explorerAccountUI');

  XPIndicatorFn({});

  if (!accountId || determiningProjectAccess) return null;
  return (
    <Page title={projectName} pageType="projectOverview">
      <Breadcrumbs className="mb-3" breadcrumbs={breadcrumbs} />
      {!isCatalogPreviewEnabled && <OverviewPageContent />}
      {isCatalogPreviewEnabled && <NewProjectOverviewPageContent />}
    </Page>
  );
};

type NewProjectOverviewPageContentProps = {};

const NewProjectOverviewPageContent: FC<NewProjectOverviewPageContentProps> = () => {
  const { environmentType } = useRawParams();
  const { hasAccountFeature } = useDbtCloudAccount();
  const { accountId, projectName, environmentId, determiningProjectAccess, projectId } =
    useDbtCloudProject();
  const { data: favoriteProjects } = useFavoriteProjects(accountId);
  const isFavorite = favoriteProjects?.some(
    (project) => project.project_id === projectId,
  );

  const navigate = useNavigate();
  const searchResultsLink = useLinkToExplorer('summitSearchResults');

  const projectLineageLinkGenerator = useLinkToExplorer('projectLineage');
  const lineageLink = projectLineageLinkGenerator(useUrlProjectParams());

  const [recommendationCount] = useRecommendationsCount({
    environmentId,
    uniqueId: undefined,
    pause: determiningProjectAccess,
  });

  const nonDevTabs: ProjectTabInfo[] = [
    { type: 'performance' },
    {
      type: 'recommendations',
      count: recommendationCount.data?.recommendations?.rules?.totalCount,
    },
  ];
  const tabs: ProjectTabInfo[] = [
    { type: 'overview' },
    ...(environmentType !== 'development' ? nonDevTabs : []),
  ];

  if (!determiningProjectAccess && !environmentId) {
    return <ProjectHasNoEnvironments />;
  }

  return (
    <>
      <div className="my-4 flex h-8 items-center ">
        <h1
          className="mt-0.5 h-[39px] flex-1 text-2xl leading-[39px] text-fgMain"
          data-testid="project-name-header"
        >
          {projectName}
        </h1>
        <div className="flex flex-row items-center gap-2">
          <FavoriteProjectToggle
            iconSize="md"
            isFavorite={isFavorite}
            projectId={projectId}
          />
          <Button
            size={Sizes.md}
            type={'secondary'}
            text={'Browse assets'}
            onClick={() => {
              navigate(
                searchResultsLink({
                  accountId: accountId!,
                  filters: JSON.stringify({
                    ...getDefaultAccountFilters(hasAccountFeature('catalogIngestion')),
                    projects: [projectId],
                  }),
                }),
              );
            }}
          />
          <Link
            to={lineageLink}
            isInternal
            className="flex-0 h-[39px]"
            buttonStyleType="primary"
            data-trackingid="explorer-view-lineage"
            data-testid="view-lineage-button"
          >
            <Icon ryecon={RyeconLineage} />
            View lineage
          </Link>
        </div>
      </div>
      <ProjectOverviewTabs tabs={tabs} show={true}>
        {(tabType) => {
          switch (tabType) {
            case 'overview':
              return (
                <div className="pb-10">
                  <NewOverviewPageContent />
                </div>
              );
            case 'performance':
              return (
                <div className="pb-10">
                  <PerformancePageContent showHeader={false} />
                </div>
              );
            case 'recommendations':
              return (
                <div className="pb-10">
                  <RecommendationsPageContent showHeader={false} />
                </div>
              );
            default:
              return null;
          }
        }}
      </ProjectOverviewTabs>
    </>
  );
};

type OverviewPageContentProps = {
  showHeader?: boolean;
};

const OverviewPageContent: FC<OverviewPageContentProps> = ({ showHeader = true }) => {
  const projectLineageLinkGenerator = useLinkToExplorer('projectLineage');
  const lineageLink = projectLineageLinkGenerator(useUrlProjectParams());
  return (
    <>
      {showHeader && (
        <div className="my-4 flex h-8 items-center">
          <h1 className="mt-0.5 h-[39px] flex-1 text-xl leading-[39px] text-fgMain">
            Overview
          </h1>

          <Link
            to={lineageLink}
            isInternal
            className="flex-0 h-[39px]"
            buttonStyleType="primary"
            data-trackingid="explorer-view-lineage"
            data-testid="view-lineage-button"
          >
            <Icon ryecon={RyeconLineage} />
            View lineage
          </Link>
        </div>
      )}
      <div className="mt-4 space-y-10">
        <ProjectDescriptionBanner />
        <div className="space-y-4">
          <LatestUpdates />
          <LineageAdUnit />
        </div>
        <PublicModelsAndMarts />
      </div>
    </>
  );
};

const NewOverviewPageContent: FC<OverviewPageContentProps> = () => {
  return (
    <>
      <div className="mt-10 space-y-10">
        <div>
          <div className="mb-2">
            <Heading size="4">Project description</Heading>
          </div>
          <ProjectDescriptionBanner showHeader={false} />
        </div>
        <ProjectAssets />
        {/* [META-4667] TODO: This should conditionally render when model query history is disabled */}
        <Marts />
        <LatestUpdates />
      </div>
    </>
  );
};
